const TRANSLATION_KEY_FIELDS = [
    'title',
    'pretitle',
    'description',
    'text',
    'prefix',
    'description_key',
    'header',
    'from_name',
    'from_initials',
    'from_address',
    'subject',
    'to_name',
    'to_address',
    'extra_to_string',
    'messenger_name',
    'messenger_text',
];

const TRANSLATION_IMAGE_FIELDS = new Set([
    'image',
    'imageUrl',
    'imageZoom',
    'leftThumb',
    'rightThumb',
    'icon',
    'iconHover',
]);

const TRANSLATION_VALUE_FIELD_KEYS = new Set([
    ...TRANSLATION_KEY_FIELDS,
    'description_key',
    ...TRANSLATION_IMAGE_FIELDS,
]);

/** i18n keys that resolve to image URLs (e.g. conversation avatars img_person1). */
export function isTranslatableImageKeyReference(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return /^img_[a-z0-9_]+$/i.test(trimmed);
}

/** Static asset paths and image URLs used as i18n keys for partner/company overrides. */
export function isTranslatableImageValue(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/^\/static\//i.test(trimmed)) return true;
    if (/^https?:\/\//i.test(trimmed)) return true;
    if (
        /\.(svg|png|jpe?g|gif|webp|ico)(\?.*)?$/i.test(trimmed) &&
        !/\s/.test(trimmed)
    ) {
        return true;
    }
    return false;
}

/** User-facing copy used as i18n keys — not colors, URLs, or static asset paths. */
export function isTranslatableStringValue(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
        return false;
    }
    if (/^rgba?\(/i.test(trimmed)) return false;
    if (/^\/static\//i.test(trimmed)) return false;
    if (/^https?:\/\//i.test(trimmed)) return false;
    if (
        /\.(svg|png|jpe?g|gif|webp|ico)(\?.*)?$/i.test(trimmed) &&
        !/\s/.test(trimmed)
    ) {
        return false;
    }
    return true;
}

function collectStringsFromValue(value, out, fieldKey = null) {
    if (value == null) return;
    if (typeof value === 'string') {
        if (fieldKey && TRANSLATION_IMAGE_FIELDS.has(fieldKey)) {
            const trimmed = value.trim();
            if (
                isTranslatableImageValue(trimmed) ||
                isTranslatableImageKeyReference(trimmed)
            ) {
                out.add(trimmed);
            }
            return;
        }
        if (
            fieldKey &&
            TRANSLATION_VALUE_FIELD_KEYS.has(fieldKey) &&
            isTranslatableStringValue(value)
        ) {
            out.add(value.trim());
        }
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectStringsFromValue(item, out, fieldKey));
        return;
    }
    if (typeof value === 'object') {
        Object.keys(value).forEach((key) => {
            collectStringsFromValue(value[key], out, key);
        });
    }
}

export function questionDefinitionToTranslationKeys(definition) {
    const out = new Set();
    let def;
    if (typeof definition === 'string') {
        try {
            def = JSON.parse(definition);
        } catch {
            return [];
        }
    } else if (definition && typeof definition === 'object') {
        if (
            definition.ui &&
            typeof definition.ui === 'object' &&
            definition.definition == null
        ) {
            // LMS preview API exposes `ui` at top level (definition cleared server-side).
            def = { ui: definition.ui };
        } else if (definition.definition != null) {
            if (typeof definition.definition === 'string') {
                try {
                    def = JSON.parse(definition.definition);
                } catch {
                    return [];
                }
            } else {
                def = definition.definition;
            }
        } else {
            def = definition;
        }
    } else {
        return [];
    }

    if (def?.ui) {
        collectStringsFromValue(def.ui, out);
    }

    return [...out].sort();
}
